import React from 'react';
import { defineMessages } from 'react-intl';
import { generatePath, Link, useParams } from 'react-router-dom';
import { CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { AppConstants, DEFAULT_FORMAT, DEFAULT_TABLE_ITEM_SIZE_LARGE, UserTypes } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { deviceKindOptions, deviceSubKindOptions, statusOptions } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useListBase from '@hooks/useListBase';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { AvatarField, BaseTable,BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime } from '@utils';
import { Button, Modal } from 'antd';

const message = defineMessages({
    objectName: 'Tablet Request',
});

const TabletRequestListPage = () => {
    const translate = useTranslate();
    const notification = useNotification();
    const queryParams = new URLSearchParams(location.search);
    const restaurantName = queryParams.get('restaurantName');
    const deviceKindOpt = translate.formatKeys(deviceKindOptions, ['label']);
    const deviceSubKindOpt = translate.formatKeys(deviceSubKindOptions, ['label']);

    const { execute: executeApprove, loading: loadingApprove } = useFetch(apiConfig.tableRequest.approve);
    const { data, mixinFuncs, queryFilter, pagination, loading } = useListBase({
        apiConfig: apiConfig.tableRequest,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE_LARGE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.additionalActionColumnButtons = () => ({
                approve: ({ id }) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.approve)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={() => {
                                Modal.confirm({
                                    title: 'Approve Tablet Request',
                                    content: 'Are you sure you want to approve this request?',
                                    okText: 'Approve',
                                    okType: 'primary',
                                    cancelText: 'Cancel',
                                    onOk: () => {
                                        executeApprove({
                                            data: {
                                                id: id,
                                            },
                                            onCompleted: (res) => {
                                                if (res.result) {
                                                    notification({ type: 'success', message: 'Approve success' });
                                                    mixinFuncs.getList();
                                                }
                                            },
                                            onError: (error) => {
                                                console.log(error);
                                            },
                                        });
                                    },
                                });
                            }}
                        >
                            <CheckOutlined
                                style={{
                                    color: '#52c41a',
                                }}
                            />
                        </Button>
                    </BaseTooltip>
                ),
                delete: ({ id }) => (
                    <BaseTooltip type="delete" objectName={translate.formatMessage(message.objectName)}>
                        <Button
                            type="link"
                            onClick={(e) => {
                                e.stopPropagation();
                                mixinFuncs.showDeleteItemConfirm(id);
                            }}
                            style={{ padding: 0 }}
                        >
                            <DeleteOutlined style={{ color: 'red' }} />
                        </Button>
                    </BaseTooltip>
                ),
            });
        },
    });

    const columns = [
        {
            title: translate.formatMessage(commonMessage.name),
            dataIndex: 'name',
        },
        {
            title: translate.formatMessage(commonMessage.deviceKind),
            dataIndex: 'kind',
            render: (kind) => {
                return <>{deviceKindOpt?.find((item) => item.value === kind)?.label}</>;
            },
        },
        {
            title: translate.formatMessage(commonMessage.deviceSubKind),
            dataIndex: 'subKind',
            render: (value) => {
                return <>{deviceSubKindOpt?.find((item) => item.value === value)?.label}</>;
            },
        },
        {
            title: translate.formatMessage(commonMessage.createdDate),
            width: 125,
            dataIndex: 'createDate',
            render: (createDate) => {
                const createDateLocal = convertUtcToLocalTime(createDate, DEFAULT_FORMAT, DEFAULT_FORMAT);
                return <div>{createDateLocal}</div>;
            },
        },

        mixinFuncs.renderStatusColumn({ width: '100px', align: 'left' }),
        mixinFuncs.renderActionColumn(
            {
                approve: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ];

    return (
        <PageWrapper
            routes={[
                { breadcrumbName: translate.formatMessage(commonMessage.restaurant), path: `/restaurants` },
                { breadcrumbName: translate.formatMessage(message.objectName) },
            ]}
        >
            <ListPage
                title={<span style={{ fontSize: '17px', paddingBottom: 8, height: 100 }}>{restaurantName}</span>}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        loading={loading}
                    />
                }
            />
        </PageWrapper>
    );
};

export default TabletRequestListPage;
